import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { StepperOrientation,MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { Firestore, collection, addDoc, doc } from '@angular/fire/firestore';
import { ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { AfterViewInit } from '@angular/core';
import confetti from 'canvas-confetti';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {BreakpointObserver} from '@angular/cdk/layout';
import { RentalApplicationService } from '../../services/rental-application.service';

@Component({
  selector: 'app-application-submission',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    HttpClientModule,
    MatStepperModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule
  ],
  templateUrl: './application-submission.component.html',
  styleUrls: ['./application-submission.component.scss'],
})
export class ApplicationSubmissionComponent implements OnInit {
  // route rentalId
  rentalId!: string;
  loading = false;
  hasCoApplicant: boolean | null = null;
  // Step FormGroups
  stepApplicant!: FormGroup;       // applicant info
  stepApplicantEmployment!: FormGroup;
  stepCoApplicant!: FormGroup;
  stepCoEmployment!: FormGroup;
  stepResidence!: FormGroup;
  stepVehicles!: FormGroup;
  stepReferences!: FormGroup;
  stepBackground!: FormGroup;
  stepSignature!: FormGroup;
  submissionSuccess = false;
  stepperOrientation: Observable<StepperOrientation>;
  private applicationService = inject(RentalApplicationService);
  @ViewChild('stepper') stepper!: MatStepper;
  // injectables
  private fb = inject(FormBuilder);
  private firestore = inject(Firestore);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  constructor(private router: Router){
    const breakpointObserver = inject(BreakpointObserver);

    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 1050px)')
      .pipe(map(({matches}) => (matches ? 'horizontal' : 'vertical')));

  }
  ngOnInit(): void {
    this.rentalId = this.route.snapshot.paramMap.get('rentalId') || '';

    // Step 1 - Applicant Info
    this.stepApplicant = this.fb.group({
      fullName: ['', Validators.required],
      dob: ['', Validators.required],
      ssn: ['', Validators.required],
      phone: ['', Validators.required],
      applicantEmail: ['', [Validators.required, Validators.email]],
      driversLicenseNum: ['', Validators.required],

      // checkboxes / toggles
      additionalOccupants: ['', Validators.required], // "Yes"/"No"
      occupantsDescription: [''],
      hasPets: ['', Validators.required], // function expects hasPets
      petsDescription: [''],
    });

    // Step 1b - Applicant Employment
    this.stepApplicantEmployment = this.fb.group({
      employerName: ['', Validators.required],
      employerAddress: [''],
      jobTitle: ['', Validators.required],
      income: ['', Validators.required],
      jobHowLong: [''],
      supervisorName: [''],
      supervisorPhone: [''],
      supervisorEmail: [''],
    });

    // Step 2 - Co-Applicant Info (optional)
    this.stepCoApplicant = this.fb.group({
      coAppFullName: [''],
      coAppSSN: [''],
      coAppDOB: [''],
      coAppPhone: [''],
      coAppEmail: ['', Validators.email],
      coAppLicenseNum: [''],
      // Co-applicant residence fields (optional)
      coAddress: [''],
      coRent: [''],
      cohowLong: [''],
      coReason: [''],
      coLandlordName: [''],
      coLandlordEmail: [''],
      coLandlordPhone: [''],
    });

    // Step 2b - Co-Applicant Employment (optional)
    this.stepCoEmployment = this.fb.group({
      coemployerName: [''],
      coemployerAddress: [''],
      cojobTitle: [''],
      coincome: [''],
      cojobHowLong: [''],
      cosupervisorName: [''],
      cosupervisorPhone: [''],
      cosupervisorEmail: [''],
    });

    // Step 3 - Residence (applicant)
    this.stepResidence = this.fb.group({
      currentAddress: ['', Validators.required],
      currentRent: ['', Validators.required],
      howLong: ['', Validators.required],
      currentReason: [''],
      currentLandlordName: [''],
      currentLandlordEmail: ['', Validators.email],
      currentLandlordPhone: [''],
    });

    // Step 4 - Vehicles (optional up to 2)
    this.stepVehicles = this.fb.group({
      vehicle1Make: [''],
      vehicle1Model: [''],
      vehicle1Year: [''],
      vehicle1Color: [''],
      vehicle1Plate: [''],

      vehicle2Make: [''],
      vehicle2Model: [''],
      vehicle2Year: [''],
      vehicle2Color: [''],
      vehicle2Plate: [''],
    });

    // Step 5 - References
    this.stepReferences = this.fb.group({
      ref1name: [''],
      ref1Phone: [''],
      ref1Relationship: [''],
      ref2name: [''],
      ref2Relationship: [''],
      ref2Phone: [''],
      ref3name: [''],
      ref3Relationship: [''],
      ref3Phone: [''],
    });

    // Step 6 - Background & checks
    this.stepBackground = this.fb.group({
      evicted: ['', Validators.required], // maps to 'evicted'
      evictionDetails: [''],
      bankruptcy: ['', Validators.required],
      bankruptcyDetails: [''],
      landlordBalance: ['', Validators.required],
      landlordBalanceDetails: [''],
      leaseViolation: ['', Validators.required],
      leaseViolationDetails: [''],
      convicted: ['', Validators.required], // maps to 'convicted'
      crimeDetails: [''],
    });

    // Step 7 - Signature
    this.stepSignature = this.fb.group({
      signatureName: ['', Validators.required],
      signatureDate: ['', Validators.required],
    });
  }
  private launchConfetti() {
    const canvas = document.getElementById('confettiCanvas') as HTMLCanvasElement;
    if (!canvas) return;

    const myConfetti = confetti.create(canvas, {
      resize: true,
      useWorker: true
    });

    myConfetti({
      particleCount: 120,
      spread: 70,
      origin: { y: 0.6 }
    });
  }
  // Helper used by template to display validation errors
  isInvalid(form: FormGroup, controlName: string) {
    const ctrl = form.get(controlName);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  // Called by Yes/No co-applicant prompt
  setHasCoApplicant(val: boolean, stepper: any) {
    this.hasCoApplicant = val;
    // move forward
    setTimeout(() => stepper.next(), 0);
  }
  goToDashboard() {
    this.router.navigate(['/']);   // or wherever your app dashboard is
  }
  // merge all data to the exact keys expected by your generatePdfAndEmail
  private buildPayload(): Record<string, any> {
    const payload: Record<string, any> = {
      // applicant fields
      ...this.stepApplicant.value,
      // applicant employment (keys expected by function)
      ...this.stepApplicantEmployment.value,

      // co-applicant (only include if present)
      ...this.stepCoApplicant.value,

      // co applicant employment
      ...this.stepCoEmployment.value,

      // residence
      ...this.stepResidence.value,

      // vehicles
      ...this.stepVehicles.value,

      // references
      ...this.stepReferences.value,

      // background
      ...this.stepBackground.value,

      // signature
      ...this.stepSignature.value,

      // metadata
      rentalId: this.rentalId,
      createdAt: new Date().toISOString(),
    };

    // The Cloud Function expects 'additionalOccupants' for checkboxes mapping
    // ensure the exact names used in checkboxGroups are present.
    // Our stepApplicant uses 'additionalOccupants' and 'hasPets' etc already.

    return payload;
  }

  async submitAll(stepper?: MatStepper) {
    // final guard: ensure required controls in every active step are valid
    const applicantValid = this.stepApplicant.valid && this.stepApplicantEmployment.valid;
    const residenceValid = this.stepResidence.valid;
    const backgroundValid = this.stepBackground.valid;
    const signatureValid = this.stepSignature.valid;
    const coOk = this.hasCoApplicant ? (this.stepCoApplicant.valid && this.stepCoEmployment.valid) : true;

    if (!applicantValid || !residenceValid || !backgroundValid || !signatureValid || !coOk) {
      // mark touched to show validation UI
      this.stepApplicant.markAllAsTouched();
      this.stepApplicantEmployment.markAllAsTouched();
      this.stepResidence.markAllAsTouched();
      this.stepBackground.markAllAsTouched();
      this.stepSignature.markAllAsTouched();
      if (this.hasCoApplicant) {
        this.stepCoApplicant.markAllAsTouched();
        this.stepCoEmployment.markAllAsTouched();
      }
      alert('Please fill out all required fields.');
      return;
    }

    this.loading = true;
    try {
      const payload = this.buildPayload();

      // ✅ NEW — submit via round-aware service
      await this.applicationService.submitApplicationForRental(
        this.rentalId,
        payload
      );

      // Cloud Function call unchanged
      const url =
        'https://us-central1-realestatesite-8b501.cloudfunctions.net/generatePdfAndEmail';
      await firstValueFrom(this.http.post(url, payload, { responseType: 'text' }));

      this.submissionSuccess = true;
      setTimeout(() => this.stepper.next(), 100);
      setTimeout(() => this.launchConfetti(), 300);

    } catch (err) {
      console.error('Submit error:', err);
      alert('Applications are currently closed for this rental.');
    } finally {
      this.loading = false;
    }
  }


  private resetAll() {
    this.stepApplicant.reset();
    this.stepApplicantEmployment.reset();
    this.stepCoApplicant.reset();
    this.stepCoEmployment.reset();
    this.stepResidence.reset();
    this.stepVehicles.reset();
    this.stepReferences.reset();
    this.stepBackground.reset();
    this.stepSignature.reset();
    this.hasCoApplicant = null;
  }
}